import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useColorMode, Skeleton, SkeletonCircle, Box, Flex } from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

// Skeleton Loader Component for Menu Items
const MenuItemSkeleton = ({ isDark, hasSubmenu = false }) => {
   return (
      <div
         className={`flex items-center justify-between px-4 py-2 rounded-xl mb-1.5 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100/50'
            }`}>
         <Flex alignItems="center" gap={3} flex="1">
            <SkeletonCircle
               size="20px"
               startColor={isDark ? 'gray.600' : 'gray.300'}
               endColor={isDark ? 'gray.700' : 'gray.400'}
            />
            <Skeleton
               height="14px"
               width="60%"
               startColor={isDark ? 'gray.600' : 'gray.300'}
               endColor={isDark ? 'gray.700' : 'gray.400'}
            />
         </Flex>
         {hasSubmenu && (
            <SkeletonCircle
               size="16px"
               startColor={isDark ? 'gray.600' : 'gray.300'}
               endColor={isDark ? 'gray.700' : 'gray.400'}
            />
         )}
      </div>
   );
};

// Skeleton Loader for Category Header
const CategorySkeleton = ({ isDark }) => {
   return (
      <div
         className={`px-4 pt-6 pb-3 rounded-r-lg border-l-2 ${isDark ? 'bg-violet-500/5 border-violet-500/50' : 'bg-indigo-100/50 border-indigo-400'
            }`}>
         <Skeleton
            height="12px"
            width="50%"
            startColor={isDark ? 'gray.600' : 'gray.300'}
            endColor={isDark ? 'gray.700' : 'gray.400'}
         />
      </div>
   );
};

// Main Skeleton Component for the entire menu
const NavigationSkeleton = ({ isDark }) => {
   return (
      <div className="space-y-0.5 px-2">
         {/* Category 1 */}
         <CategorySkeleton isDark={isDark} />
         <MenuItemSkeleton isDark={isDark} />
         <MenuItemSkeleton isDark={isDark} hasSubmenu={true} />
         <MenuItemSkeleton isDark={isDark} />

         {/* Separator */}
         <div className="relative w-full my-6 px-2">
            <div
               className={`h-[1.5px] w-full rounded-full ${isDark
                  ? 'bg-gradient-to-r from-transparent via-slate-600/60 to-transparent'
                  : 'bg-gradient-to-r from-transparent via-gray-300/80 to-transparent'
                  }`}
            />
         </div>

         {/* Category 2 */}
         <CategorySkeleton isDark={isDark} />
         <MenuItemSkeleton isDark={isDark} hasSubmenu={true} />
         <MenuItemSkeleton isDark={isDark} />
         <MenuItemSkeleton isDark={isDark} hasSubmenu={true} />

         {/* More items */}
         <MenuItemSkeleton isDark={isDark} />
         <MenuItemSkeleton isDark={isDark} />
      </div>
   );
};

export function SidebarLinks(props) {
   const location = useLocation();
   const navigate = useNavigate();

   // Get theme from Chakra UI
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [openSubmenus, setOpenSubmenus] = useState({});
   const { routes, onClose, showShortcuts, setShowShortcuts, isSubmenu = false, isLoading = false } = props;
   // Hide shortcuts when route changes
   useEffect(() => {
      setShowShortcuts(false);
   }, [location.pathname]);

   useEffect(() => {
      const handleKeyDown = (e) => {
         if (e.key === 'Alt') {
            setShowShortcuts((prev) => !prev);
            e.preventDefault();
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, []);

   // Auto-open parent menus when their children are active
   useEffect(() => {
      const newOpenStates = {};
      props.routes?.forEach((route) => {
         if (route.items) {
            const hasActiveChild = route.items.some((child) => {
               if (child.items) {
                  return child.items.some((subChild) => activeRoute(subChild));
               }
               return activeRoute(child);
            });

            if (hasActiveChild) {
               newOpenStates[route.name] = true;
            }
         }
      });
      setOpenSubmenus((prev) => ({ ...prev, ...newOpenStates }));
   }, [location.pathname]);

   const toggleSubmenu = (name, route) => {
      setOpenSubmenus((prev) => {
         const newState = { ...prev };

         // Close all other submenus
         Object.keys(newState).forEach((key) => {
            if (key !== name) newState[key] = false;
         });

         // Toggle current submenu
         const isOpening = !prev[name];
         newState[name] = isOpening;

         // If opening and has default child, navigate to it
         if (isOpening && route.items) {
            const defaultChild = route.items.find((child) => child.isDefault);
            if (defaultChild && !activeRoute(route)) {
               navigate(route.layout + defaultChild.path);
            }
         }

         return newState;
      });
   };

   // Check if any child route is active
   const hasActiveChild = (route) => {
      if (!route.items) return false;
      return route.items.some((child) => {
         if (child.items) return hasActiveChild(child);
         return activeRoute(child);
      });
   };

   const activeRoute = (route) => {
      const currentPath = location.pathname;

      if (route.parentFor) {
         return route.parentFor.some((parentPath) => currentPath.includes(parentPath));
      }

      if (route.path) {
         if (route.exactMatch) {
            return currentPath === route.layout + route.path;
         }
         if (route.activeSegment) {
            return currentPath.includes(route.activeSegment);
         }
         return currentPath.includes(route.path);
      }

      return false;
   };

   const createLinks = (routes, onClose, isSubmenu = false, isLastItem = false) => {
      return routes?.map((route, index) => {
         const isLast = index === routes.length - 1;

         if (route.type === 'separator') {
            return (
               <div key={`separator-${index}`} className="relative w-full my-6 px-2">
                  <div
                     className={`h-[1.5px] w-full rounded-full ${isDark
                        ? 'bg-gradient-to-r from-transparent via-slate-600/60 to-transparent'
                        : 'bg-gradient-to-r from-transparent via-gray-300/80 to-transparent'
                        }`}
                  />
               </div>
            );
         }

         if (route.category) {
            return (
               <React.Fragment key={index}>
                  <div
                     className={`text-[11px] font-bold tracking-[0.1em] uppercase px-4 pt-6 pb-3 ${isDark
                        ? 'text-violet-400 bg-gradient-to-r from-violet-500/5 to-transparent'
                        : 'text-indigo-700 bg-gradient-to-r from-indigo-100/50 to-transparent'
                        } rounded-r-lg border-l-2 ${isDark ? 'border-violet-500/50' : 'border-indigo-400'}`}>
                     {route.name}
                  </div>
                  {createLinks(route.items, onClose)}
               </React.Fragment>
            );
         } else if (route.items) {
            const isParentActive = hasActiveChild(route);
            const isSubmenuOpen = openSubmenus[route.name];
            const shouldShowActiveBg = isParentActive && !isSubmenuOpen;

            return (
               <div key={index} className="mb-1.5">
                  <button
                     onClick={() => toggleSubmenu(route.name, route)}
                     className={`w-full flex items-center justify-between px-4 py-2 rounded-xl transition-all duration-300 group relative overflow-hidden ${shouldShowActiveBg
                        ? isDark
                           ? 'bg-gradient-to-r from-teal-500/20 via-cyan-500/15 to-transparent border border-teal-400/50 shadow-lg shadow-teal-500/10'
                           : 'bg-gradient-to-r from-teal-50 via-cyan-50 to-transparent border border-teal-300 shadow-md'
                        : isDark
                           ? 'hover:bg-gradient-to-r hover:from-teal-500/15 hover:via-cyan-500/10 hover:to-transparent border border-transparent hover:border-teal-400/40 hover:shadow-lg hover:shadow-teal-500/10'
                           : 'hover:bg-gradient-to-r hover:from-teal-50/80 hover:via-cyan-50/60 hover:to-transparent border border-transparent hover:border-teal-300/60 hover:shadow-md'
                        }`}>
                     {/* Hover glow effect */}
                     <div
                        className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isDark
                           ? 'bg-gradient-to-r from-teal-500/5 to-cyan-500/5'
                           : 'bg-gradient-to-r from-teal-50/50 to-cyan-50/50'
                           }`}
                     />

                     <div className="flex items-center gap-3 relative z-10">
                        <div
                           className={`transition-all duration-300 transform group-hover:scale-110 ${shouldShowActiveBg
                              ? isDark
                                 ? 'text-teal-400 drop-shadow-[0_0_8px_rgba(45,212,191,0.5)]'
                                 : 'text-teal-600 drop-shadow-sm'
                              : isDark
                                 ? 'text-slate-400 group-hover:text-teal-400'
                                 : 'text-gray-600 group-hover:text-teal-600'
                              }`}>
                           {route.icon}
                        </div>
                        <div className="flex items-center gap-2">
                           <span
                              className={`text-sm font-semibold transition-all duration-300 ${shouldShowActiveBg
                                 ? isDark
                                    ? 'text-teal-300 font-bold'
                                    : 'text-teal-700 font-bold'
                                 : isDark
                                    ? 'text-slate-300 group-hover:text-white'
                                    : 'text-gray-700 group-hover:text-gray-900'
                                 }`}>
                              {route.name}
                           </span>
                           {showShortcuts && route.shortcut && (
                              <span
                                 className={`text-[12px] font-bold px-2 rounded-md border transition-all duration-300 ${isDark
                                    ? 'text-blue-300 border-blue-400/60 bg-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.3)]'
                                    : 'text-blue-700 border-blue-400 bg-blue-100 shadow-sm'
                                    }`}>
                                 {route.shortcut}
                              </span>
                           )}
                        </div>
                     </div>

                     <div
                        className={`transition-all duration-300 relative z-10 ${isSubmenuOpen ? 'rotate-0' : '-rotate-90'
                           } ${shouldShowActiveBg
                              ? isDark
                                 ? 'text-teal-400'
                                 : 'text-teal-600'
                              : isDark
                                 ? 'text-slate-500 group-hover:text-teal-400'
                                 : 'text-gray-500 group-hover:text-teal-600'
                           }`}>
                        <FiChevronDown size="18px" />
                     </div>
                  </button>

                  {isSubmenuOpen && (
                     <div className="mt-2 ml-6 relative">
                        {/* Gradient vertical dashed line */}
                        <div
                           className="absolute left-0 top-0 w-0.5 border-l-2 border-dashed"
                           style={{
                              height: '100%',
                              borderColor: isDark ? 'rgba(45, 212, 191, 0.4)' : 'rgba(20, 184, 166, 0.3)',
                              background: isDark
                                 ? 'linear-gradient(to bottom, rgba(45, 212, 191, 0.2), rgba(34, 211, 238, 0.2))'
                                 : 'linear-gradient(to bottom, rgba(45, 212, 191, 0.3), rgba(34, 211, 238, 0.3))',
                           }}
                        />

                        <div className="space-y-1">
                           {route.items.map((child, childIndex) => (
                              <div key={childIndex} className="relative">
                                 {/* Horizontal connector with glow */}
                                 <div
                                    className="absolute left-0 top-1/2 w-4 h-0.5 border-b-2 border-dashed -translate-y-1/2"
                                    style={{
                                       borderColor: isDark ? 'rgba(45, 212, 191, 0.4)' : 'rgba(20, 184, 166, 0.3)',
                                    }}
                                 />

                                 <div className="pl-5">
                                    {child.layout === '/admin' ||
                                       child.layout === '/auth' ||
                                       child.layout === '/rtl' ? (
                                       <NavLink
                                          to={child.layout + child.path}
                                          onClick={() => {
                                             onClose && onClose();
                                             setShowShortcuts(false);
                                          }}>
                                          {(() => {
                                             const isActive = activeRoute(child);
                                             return (
                                                <div
                                                   className={`flex items-center px-3 py-2 rounded-lg transition-all duration-300 mb-1 group relative overflow-hidden ${isActive
                                                      ? isDark
                                                         ? 'bg-gradient-to-r from-teal-500/20 via-cyan-500/15 to-transparent border-2 border-teal-400/60 shadow-xl shadow-teal-500/20'
                                                         : 'bg-gradient-to-r from-teal-100 via-cyan-100 to-transparent border-2 border-teal-400 shadow-lg shadow-teal-200/50'
                                                      : isDark
                                                         ? 'hover:bg-gradient-to-r hover:from-teal-500/15 hover:via-cyan-500/10 hover:to-transparent border border-transparent hover:border-teal-400/40 hover:shadow-lg hover:shadow-teal-500/10'
                                                         : 'hover:bg-gradient-to-r hover:from-teal-50/80 hover:via-cyan-50/60 hover:to-transparent border border-transparent hover:border-teal-300/60 hover:shadow-md'
                                                      }`}>
                                                   {/* Active indicator line */}
                                                   {isActive && (
                                                      <div
                                                         className={`absolute left-0 top-0 bottom-0 w-1 rounded-r-full ${isDark
                                                            ? 'bg-gradient-to-b from-teal-400 via-cyan-400 to-cyan-500 shadow-[0_0_12px_rgba(45,212,191,0.6)]'
                                                            : 'bg-gradient-to-b from-teal-500 via-cyan-500 to-cyan-600 shadow-md'
                                                            }`}
                                                      />
                                                   )}

                                                   {/* Hover glow effect */}
                                                   <div
                                                      className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isDark
                                                         ? 'bg-gradient-to-r from-teal-500/5 via-cyan-500/5 to-transparent'
                                                         : 'bg-gradient-to-r from-teal-50/70 via-cyan-50/50 to-transparent'
                                                         }`}
                                                   />

                                                   <div className="flex items-center gap-3 relative z-10">
                                                      <div
                                                         className={`transition-all duration-300 transform group-hover:scale-110 group-hover:rotate-6 ${isActive
                                                            ? isDark
                                                               ? 'text-teal-400 drop-shadow-[0_0_10px_rgba(45,212,191,0.6)]'
                                                               : 'text-teal-600 drop-shadow-md'
                                                            : isDark
                                                               ? 'text-slate-500 group-hover:text-teal-400'
                                                               : 'text-gray-500 group-hover:text-teal-600'
                                                            }`}>
                                                         {child.icon}
                                                      </div>
                                                      <div className="flex items-center gap-2">
                                                         <span
                                                            className={`text-sm transition-all duration-300 ${isActive
                                                               ? isDark
                                                                  ? 'text-teal-300 font-bold tracking-wide'
                                                                  : 'text-teal-700 font-bold tracking-wide'
                                                               : isDark
                                                                  ? 'text-slate-300 font-medium group-hover:text-white group-hover:font-semibold'
                                                                  : 'text-gray-700 font-medium group-hover:text-gray-900 group-hover:font-semibold'
                                                               }`}>
                                                            {child.name}
                                                         </span>
                                                         {showShortcuts && child.shortcut && (
                                                            <span
                                                               className={`text-[10px] font-bold px-2 py-0.5 rounded-md border transition-all duration-300 ${isDark
                                                                  ? 'text-blue-300 border-blue-400/60 bg-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.3)]'
                                                                  : 'text-blue-700 border-blue-400 bg-blue-100 shadow-sm'
                                                                  }`}>
                                                               {child.shortcut}
                                                            </span>
                                                         )}
                                                      </div>
                                                   </div>
                                                </div>
                                             );
                                          })()}
                                       </NavLink>
                                    ) : (
                                       createLinks([child], onClose, true)
                                    )}
                                 </div>
                              </div>
                           ))}
                        </div>
                     </div>
                  )}
               </div>
            );
         } else if (route.layout === '/admin' || route.layout === '/auth' || route.layout === '/rtl') {
            const isActive = activeRoute(route);

            return (
               <NavLink
                  key={index}
                  to={route.layout + route.path}
                  onClick={() => {
                     onClose && onClose();
                     setShowShortcuts(false);
                  }}>
                  <div
                     className={`flex items-center px-4 py-2 rounded-xl transition-all duration-300 mb-1.5 group relative overflow-hidden ${isActive
                        ? isDark
                           ? 'bg-gradient-to-r from-teal-500/20 via-cyan-500/15 to-transparent border-2 border-teal-400/60 shadow-xl shadow-teal-500/20'
                           : 'bg-gradient-to-r from-teal-100 via-cyan-100 to-transparent border-2 border-teal-400 shadow-lg shadow-teal-200/50'
                        : isDark
                           ? 'hover:bg-gradient-to-r hover:from-teal-500/15 hover:via-cyan-500/10 hover:to-transparent border border-transparent hover:border-teal-400/40 hover:shadow-lg hover:shadow-teal-500/10'
                           : 'hover:bg-gradient-to-r hover:from-teal-50/80 hover:via-cyan-50/60 hover:to-transparent border border-transparent hover:border-teal-300/60 hover:shadow-md'
                        }`}>
                     {/* Active indicator line */}
                     {isActive && (
                        <div
                           className={`absolute left-0 top-0 bottom-0 w-1 rounded-r-full ${isDark
                              ? 'bg-gradient-to-b from-teal-400 via-cyan-400 to-cyan-500 shadow-[0_0_12px_rgba(45,212,191,0.6)]'
                              : 'bg-gradient-to-b from-teal-500 via-cyan-500 to-cyan-600 shadow-md'
                              }`}
                        />
                     )}

                     {/* Hover glow effect */}
                     <div
                        className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isDark
                           ? 'bg-gradient-to-r from-teal-500/5 via-cyan-500/5 to-transparent'
                           : 'bg-gradient-to-r from-teal-50/70 via-cyan-50/50 to-transparent'
                           }`}
                     />

                     <div className="flex items-center gap-3 relative z-10">
                        <div
                           className={`transition-all duration-300 transform group-hover:scale-110 group-hover:rotate-6 ${isActive
                              ? isDark
                                 ? 'text-teal-400 drop-shadow-[0_0_8px_rgba(45,212,191,0.5)]'
                                 : 'text-teal-600 drop-shadow-sm'
                              : isDark
                                 ? 'text-slate-400 group-hover:text-teal-400'
                                 : 'text-gray-600 group-hover:text-teal-600'
                              }`}>
                           {route.icon}
                        </div>
                        <div className="flex items-center gap-2">
                           <span
                              className={`text-sm font-semibold transition-all duration-300 ${isActive
                                 ? isDark
                                    ? 'text-teal-300 font-bold tracking-wide'
                                    : 'text-teal-700 font-bold tracking-wide'
                                 : isDark
                                    ? 'text-slate-300 font-medium group-hover:text-white group-hover:font-semibold'
                                    : 'text-gray-700 font-medium group-hover:text-gray-900 group-hover:font-semibold'
                                 }`}>
                              {route.name}
                           </span>
                           {showShortcuts && route.shortcut && (
                              <span
                                 className={`text-[12px] font-bold px-2 rounded-md border transition-all duration-300 ${isDark
                                    ? 'text-blue-300 border-blue-400/60 bg-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.3)]'
                                    : 'text-blue-700 border-blue-400 bg-blue-100 shadow-sm'
                                    }`}>
                                 {route.shortcut}
                              </span>
                           )}
                        </div>
                     </div>
                  </div>
               </NavLink>
            );
         }
         return null;
      });
   };

   // Show skeleton loader if loading or routes not available
   if (isLoading || !routes || routes.length === 0) {
      return <NavigationSkeleton isDark={isDark} />;
   }

   return Array.isArray(routes) ? <div className="space-y-0.5 px-2">{createLinks(routes)}</div> : null;
}

export default SidebarLinks;
